/*! JointJS+ v4.2.2 (2026-01-22) - HTML5 Diagramming Framework

Copyright (c) 2025 client IO

This Source Code Form is subject to the terms of the JointJS+
License, v. 2.0. If a copy of the JointJS+ License was not
distributed with this file, You can obtain one at
https://www.jointjs.com/license or from the JointJS+ archive as was
distributed by client IO. See the LICENSE file.
*/
import { ui, dia, util } from '@joint/plus';
import { stencilGroups, stencilShapes } from '../config/stencil';
import { cellNamespace } from '../shapes/app-shapes';
var HIGHLIGHT_COLOR = '#F4F7FB';
// Define a custom highlighter for the stencil hover effect
var StencilBackground = dia.HighlighterView.extend({
    tagName: 'rect',
    attributes: {
        'stroke': 'none',
        'fill': 'transparent',
        'pointer-events': 'none',
        'rx': 4,
        'ry': 4,
    },
    style: {
        transition: 'fill 400ms'
    },
    options: {
        padding: 0,
        color: 'gray',
        width: null,
        height: null,
        layer: dia.Paper.Layers.BACK
    },
    // Method called to highlight a CellView
    highlight: function (cellView, _node) {
        var _this = this;
        var _a = this.options, padding = _a.padding, width = _a.width, height = _a.height;
        var bbox = cellView.model.getBBox();
        // Highlighter is always rendered relatively to the CellView origin
        bbox.x = bbox.y = 0;
        // Custom width and height can be set
        if (Number.isFinite(width)) {
            bbox.x = (bbox.width - width) / 2;
            bbox.width = width;
        }
        if (Number.isFinite(height)) {
            bbox.y = (bbox.height - height) / 2;
            bbox.height = height;
        }
        // Increase the size of the highlighter
        bbox.inflate(padding);
        this.vel.attr(bbox.toJSON());
        // Change the color of the highlighter (allow transition)
        util.nextFrame(function () { return _this.vel.attr('fill', _this.options.color); });
    }
});
var StencilService = /** @class */ (function () {
    function StencilService(stencilContainer) {
        this.stencilContainer = stencilContainer;
    }
    StencilService.prototype.create = function (paperScroller, snaplines) {
        var _this = this;
        this.stencil = new ui.Stencil({
            paper: paperScroller,
            snaplines: snaplines,
            width: 240,
            height: null,
            groups: this.getStencilGroups(),
            dropAnimation: true,
            groupsToggleButtons: true,
            paperOptions: function () {
                return {
                    model: new dia.Graph({}, {
                        cellNamespace: cellNamespace
                    }),
                    cellViewNamespace: cellNamespace
                };
            },
            search: {
                '*': ['type', 'name']
            },
            layout: {
                columns: 4,
                marginX: 10,
                marginY: 24,
                columnGap: 20,
                rowGap: 24,
                rowHeight: 24,
                columnWidth: 36,
                resizeToFit: true
            },
            dragStartClone: function (cell) {
                var clone = _this.createFromStencilElement(cell);
                clone.attr({
                    label: {
                        text: cell.get('name')
                    }
                });
                clone.unset('name');
                return clone;
            },
            el: this.stencilContainer
        });
        this.stencil.render();
        this.stencil.on({
            'element:dragstart': function () { return _this.tooltip.disable(); },
            'element:dragend': function () { return _this.tooltip.enable(); },
        });
        // We create a single tooltip paper that will be reused for all tooltips
        this.tooltipGraph = new dia.Graph({}, { cellNamespace: cellNamespace });
        this.tooltipPaper = new dia.Paper({
            model: this.tooltipGraph,
            cellViewNamespace: cellNamespace,
            width: 140,
            height: 120,
            async: true,
            autoFreeze: true,
            overflow: true,
            sorting: dia.Paper.sorting.NONE
        });
        this.initializeStencilTooltip.call(this);
        this.startHoverListener();
    };
    StencilService.prototype.createFromStencilElement = function (el) {
        var clone = el.clone();
        clone.prop(clone.get('targetAttributes'));
        clone.removeProp('targetAttributes');
        return clone;
    };
    StencilService.prototype.buildTooltipContent = function (cell) {
        var _a = this, tooltipGraph = _a.tooltipGraph, tooltipPaper = _a.tooltipPaper;
        // Add a copy of the cell to the tooltip graph
        // Note: We don't have to care about the position of the cell
        // because the tooltip paper will be transformed to fit the cell
        tooltipGraph.resetCells([cell.clone()]);
        var shapeNameEl = document.createElement('span');
        shapeNameEl.append(document.createTextNode(cell.get('name')));
        var documentFragment = document.createDocumentFragment();
        documentFragment.append(tooltipPaper.el, shapeNameEl);
        tooltipPaper.transformToFitContent({
            padding: 5,
            contentArea: cell.getBBox(),
            verticalAlign: 'middle',
            horizontalAlign: 'middle'
        });
        return documentFragment;
    };
    StencilService.prototype.initializeStencilTooltip = function () {
        var _this = this;
        this.tooltip = new ui.Tooltip({
            target: '[model-id]',
            rootTarget: this.stencil.el,
            // Tooltip container denotes the area where the tooltip can be shown
            // It's adding a padding on the top and the bottom of the paper area.
            container: this.stencilContainer,
            content: function (el) {
                var groups = Object.keys(_this.getStencilGroups());
                var graphs = groups.map(function (group) { return _this.stencil.getGraph(group); });
                var stencilElement = null;
                for (var _i = 0, graphs_1 = graphs; _i < graphs_1.length; _i++) {
                    var graph = graphs_1[_i];
                    var foundElement = graph.getCell(el.getAttribute('model-id'));
                    if (!foundElement)
                        continue;
                    stencilElement = foundElement;
                }
                if (!stencilElement) {
                    // The element should be always found
                    return false;
                }
                return _this.buildTooltipContent(_this.createFromStencilElement(stencilElement));
            },
            position: ui.Tooltip.TooltipPosition.Left,
            positionSelector: '.stencil-container',
            padding: 10,
            animation: {
                duration: '250ms'
            }
        });
    };
    StencilService.prototype.setShapes = function () {
        this.stencil.load(this.getStencilShapes());
    };
    StencilService.prototype.getStencilGroups = function () {
        return stencilGroups;
    };
    StencilService.prototype.getStencilShapes = function () {
        return stencilShapes;
    };
    StencilService.prototype.startHoverListener = function () {
        this.stencil.on({
            'group:element:mouseenter': function (_, elementView) {
                StencilBackground.add(elementView, 'root', 'stencil-highlight', {
                    padding: 4,
                    width: 36,
                    height: 36,
                    color: HIGHLIGHT_COLOR
                });
            },
            'group:element:mouseleave': function (groupPaper) {
                StencilBackground.removeAll(groupPaper);
            },
            // Remove all highlights when the user starts dragging an element
            'group:element:pointerdown': function (groupPaper) {
                StencilBackground.removeAll(groupPaper);
            }
        });
    };
    return StencilService;
}());
export { StencilService };
